import { useSyncExternalStore } from 'react';

export type Font = 'sans' | 'serif';

const STORAGE_KEY = 'nula_font';

/**
 * Body font preference.
 *
 * Deliberately separate from useTheme rather than folded into it: theme also
 * tints the native window, broadcasts to other Tauri windows, and follows the
 * OS via a `system` tri-state. Font has no equivalent of any of those, so
 * sharing the hook would mean carrying branches that never apply.
 *
 * Backed by a module-level store for the same reason useTheme is — per-component
 * state let a stale instance re-apply its own value and clobber the user's
 * selection. One store, one writer.
 */

let currentFont: Font = 'sans';
const listeners = new Set<() => void>();
let initialized = false;

function emit() {
  for (const l of listeners) l();
}

function readStored(): Font {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'serif' ? 'serif' : 'sans';
  } catch {
    return 'sans';
  }
}

/** Sans is the token default, so it is expressed as the absence of the attribute. */
function apply(font: Font) {
  if (font === 'serif') {
    document.documentElement.setAttribute('data-font', 'serif');
  } else {
    document.documentElement.removeAttribute('data-font');
  }
}

function persist(font: Font) {
  try {
    localStorage.setItem(STORAGE_KEY, font);
  } catch {
    // Private mode or a webview without storage — the choice just won't stick.
  }
}

function setFontStore(font: Font) {
  currentFont = font;
  apply(font);
  persist(font);
  emit();
}

/** Read lazily on first subscribe, not at module load — matches useTheme. */
function initStore() {
  if (initialized) return;
  initialized = true;
  currentFont = readStored();
  apply(currentFont);
}

function subscribe(listener: () => void) {
  initStore();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Font {
  initStore();
  return currentFont;
}

/** Test-only: the module store outlives a render, so isolation needs a reset. */
export function resetFontStoreForTests() {
  currentFont = 'sans';
  initialized = false;
  listeners.clear();
}

export function useFont() {
  const font = useSyncExternalStore(subscribe, getSnapshot, () => 'sans' as Font);
  return { font, setFont: setFontStore };
}
